import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { paymentService, PaymentProduct } from '@/features/subscription/services/paymentService';

// State type
export type PaymentStatus =
  | 'idle' // Initial state, or after completion/failure/close
  | 'loading' // Fetching prices or opening checkout
  | 'checkout_ready' // Paddle iframe loaded and ready for updates
  | 'processing' // Payment submitted, waiting for Paddle confirmation
  | 'verifying' // Backend confirmation is pending after Paddle success
  | 'completed' // Payment successful
  | 'failed'; // Payment failed or checkout error

interface PaymentState {
  status: PaymentStatus;
  prices: PriceObject | null;
  rawMonthlyPrices: PaddlePrice[] | null;
  rawYearlyPrices: PaddlePrice[] | null;
  rawWeeklyPrices: PaddlePrice[] | null;
  isLoadingPrices: boolean;
  hadFreeTrial: boolean;
  hadWeeklyDiscount: boolean;
  pricesError: string | null;
  selectedPriceId: string | null;
  interval: 'month' | 'year' | 'week';
  seats: number;
}

// Initial state
const initialState: PaymentState = {
  status: 'idle',
  prices: null,
  rawMonthlyPrices: null,
  rawYearlyPrices: null,
  rawWeeklyPrices: null,
  isLoadingPrices: false,
  hadFreeTrial: false,
  hadWeeklyDiscount: false,
  pricesError: null,
  selectedPriceId: null,
  interval: 'month',
  seats: 1,
};

export const fetchPrices = createAsyncThunk(
  'payment/fetchPrices',
  async (customerId: string | undefined, { rejectWithValue }) => {
    try {
      const response = await paymentService.getProducts();
      const data = await response.data;

      // Support both array and { data: [] } response shapes
      const rawProducts = Array.isArray(data)
        ? data
        : (data as { data?: PaymentProduct[] } | undefined)?.data;
      const products: PaymentProduct[] = Array.isArray(rawProducts)
        ? rawProducts
        : [];
      const activePrices = products
        .map(mapProductToPrice)
        .filter((price) => price.status === 'active');

      // Keep these raw price arrays
      const monthlyPrices = activePrices.filter(
        (price: PaddlePrice) => price.billing_cycle?.interval === 'month'
      );

      const yearlyPrices = activePrices.filter(
        (price: PaddlePrice) => price.billing_cycle?.interval === 'year'
      );

      const weeklyPrices = activePrices.filter(
        (price: PaddlePrice) => price.billing_cycle?.interval === 'week'
      );

      const hasTrial =
        activePrices.length > 0 &&
        activePrices.every((price: PaddlePrice) => price.trial_period != null);

      // Format for the PriceObject structure
      const formattedPrices = formatPricesToObject(monthlyPrices, yearlyPrices, weeklyPrices);
      // Return both formatted and raw prices in the payload
      return {
        prices: formattedPrices,
        rawMonthly: monthlyPrices, // <-- Include raw monthly
        rawYearly: yearlyPrices, // <-- Include raw yearly
        rawWeekly: weeklyPrices, // <-- Include raw weekly
        hadFreeTrial: !hasTrial,
      };
    } catch (error) {
      console.error('Failed to fetch prices:', error);
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to fetch prices'
      );
    }
  },
  {
    condition: (_customerId, { getState }) => {
      const state = getState() as { payment: PaymentState };
      if (state.payment.isLoadingPrices || state.payment.prices) {
        return false;
      }
    },
  }
);

const mapProductToPrice = (product: PaymentProduct): PaddlePrice => {
  const minQuantity =
    product.quantity?.minimum ?? product.customData?.minQuantity ?? 1;
  const maxQuantity =
    product.quantity?.maximum ?? product.customData?.maxQuantity ?? 1;
  const trialPeriod =
    product.trialPeriod ??
    (product.customData?.trial === true
      ? {
          interval: 'day',
          frequency: 7,
        }
      : undefined);

  return {
    id: product.id,
    product_id: product.productId,
    type: product.type,
    description: product.description,
    name: product.name,
    billing_cycle: {
      interval: product.billingCycle.interval,
      frequency: product.billingCycle.frequency,
    },
    trial_period: trialPeriod,
    trial_period_unit_price: product.trialPeriodUnitPrice
      ? {
          amount: Number(product.trialPeriodUnitPrice.amount),
          currency_code: product.trialPeriodUnitPrice.currencyCode,
        }
      : undefined,
    unit_price: {
      amount: Number(product.unitPrice.amount),
      currency_code: product.unitPrice.currencyCode,
    },
    status: product.status,
    quantity: {
      minimum: minQuantity,
      maximum: maxQuantity,
    },
    custom_data: {
      discountId: product.customData?.discountId,
      discountAmount: product.customData?.discountPercentage,
      priceType: product.customData?.priceType,
      productType: product.customData?.productType,
      title: product.customData?.title,
    },
  };
};

const formatPricesToObject = (
  monthlyPrices: PaddlePrice[],
  yearlyPrices: PaddlePrice[],
  weeklyPrices: PaddlePrice[]
): PriceObject => {
  const formattedMonthly: Price[] = monthlyPrices.map((price: PaddlePrice) => ({
    id: price.id,
    type: price.quantity.maximum > 1 ? 'multiple' : 'single',
    billing: 'month',
    name: price.name,
    price: price.unit_price.amount / 100,
    currency: price.unit_price.currency_code,
    min_quantity: price.quantity.minimum,
    max_quantity: price.quantity.maximum,
    discountId: price.custom_data?.discountId,
    discountAmount: price.custom_data?.discountAmount,
  }));

  const formattedYearly: Price[] = yearlyPrices.map(
    (price: PaddlePrice) => {
      return {
        id: price.id,
        type: price.quantity.maximum > 1 ? 'multiple' : 'single',
        billing: 'year',
        name: price.name,
        price: price.unit_price.amount / 100,
        currency: price.unit_price.currency_code,
        min_quantity: price.quantity.minimum,
        max_quantity: price.quantity.maximum,
        description: price.description,
        discountId: price.custom_data?.discountId,
        discountAmount: price.custom_data?.discountAmount,
      };
    }
  );

  const formattedWeekly: Price[] = weeklyPrices.map(
    (price: PaddlePrice) => {
      return {
        id: price.id,
        type: price.quantity.maximum > 1 ? 'multiple' : 'single',
        billing: 'week',
        name: price.name,
        price: price.unit_price.amount / 100,
        currency: price.unit_price.currency_code,
        min_quantity: price.quantity.minimum,
        max_quantity: price.quantity.maximum,
        description: price.description,
        discountId: price.custom_data?.discountId,
        discountAmount: price.custom_data?.discountAmount,
      };
    }
  );

  return {
    single: {
      month: formattedMonthly.find((price) => price.type === 'single'),
      year: formattedYearly.find((price) => price.type === 'single'),
      week: formattedWeekly.find((price) => price.type === 'single'),
    },
    multiple: {
      month: formattedMonthly.find((price) => price.type === 'multiple'),
      year: formattedYearly.find((price) => price.type === 'multiple'),
      week: formattedWeekly.find((price) => price.type === 'multiple'),
    },
  };
};

const paymentSlice = createSlice({
  name: 'payment',
  initialState,
  reducers: {
    setPaymentStatus: (state, action: PayloadAction<PaymentStatus>) => {
      state.status = action.payload;
    },
    setHadWeeklyDiscount: (state, action: PayloadAction<boolean>) => {
      state.hadWeeklyDiscount = action.payload;
    },
    setPaymentDetails: (
      state,
      action: PayloadAction<{
        selectedPriceId: string | null | undefined;
        interval: 'month' | 'year' | 'week';
        seats: number;
      }>
    ) => {
      state.selectedPriceId = action.payload.selectedPriceId || null;
      state.interval = action.payload.interval;
      state.seats = action.payload.seats;
    },
    resetPaymentDetails: (state) => {
      state.selectedPriceId = null;
      state.interval = 'month';
      state.seats = 1;
      state.status = 'idle';
    },
    clearPrices: (state) => {
      state.prices = null;
      state.rawMonthlyPrices = null;
      state.rawYearlyPrices = null;
      state.rawWeeklyPrices = null;
      state.isLoadingPrices = false;
      state.hadFreeTrial = false;
      state.hadWeeklyDiscount = false;
      state.pricesError = null;
      state.status = 'idle';
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPrices.pending, (state) => {
        state.isLoadingPrices = true;
        state.pricesError = null;
        state.status = 'loading';
        // Optionally clear old prices on pending if desired
        // state.prices = null;
        // state.rawMonthlyPrices = null;
        // state.rawYearlyPrices = null;
      })
      .addCase(fetchPrices.fulfilled, (state, action) => {
        state.isLoadingPrices = false;
        state.status = 'checkout_ready';
        state.prices = action.payload.prices;
        state.rawMonthlyPrices = action.payload.rawMonthly; // <-- Store raw monthly
        state.rawYearlyPrices = action.payload.rawYearly; // <-- Store raw yearly
        state.rawWeeklyPrices = action.payload.rawWeekly; // <-- Store raw weekly
        state.hadFreeTrial = action.payload.hadFreeTrial;
        state.pricesError = null;
      })
      .addCase(fetchPrices.rejected, (state, action) => {
        state.isLoadingPrices = false;
        state.pricesError =
          (action.payload as string) ?? // Use payload from rejectWithValue
          action.error.message ?? // Fallback to error message
          'Failed to fetch prices';
        // Clear data on error
        state.prices = null;
        state.rawMonthlyPrices = null;
        state.rawYearlyPrices = null;
        state.rawWeeklyPrices = null;
        state.status = 'failed';
      });
  },
});

// Actions
export const {
  setPaymentStatus,
  clearPrices,
  setPaymentDetails,
  resetPaymentDetails,
  setHadWeeklyDiscount,
} = paymentSlice.actions;

export const selectPayment = (state: { payment: PaymentState }) =>
  state.payment;

// Reducer
export default paymentSlice.reducer;
